/**
 * The builder's checklist, from the draft and its quote. "Fix" checks stop
 * Create (the brief can't be priced, the advertiser isn't known, or a flagged
 * rate has no reason yet); "review" checks are the quote's warnings and notes,
 * worth reading but never blocking. Pure, so the summary and tests agree.
 */

import type { LiveQuoteStatus } from './useLiveQuote';
import type { GuardrailFinding, MediaPlanQuote, NeedsAdvertiserStub } from './types';
import { findingsMissingReasons, reasonKeyForFinding, segmentIdsByKey, type DraftIssue, type PlanDraft } from './planDraft';

export interface PlanCheck {
  id: string;
  tone: 'fix' | 'review';
  message: string;
  /** The builder field it is about (see planDraft `fieldId`); clicking the check moves focus there. */
  field?: string;
}

export interface PlanChecksInput {
  draft: PlanDraft;
  issues: readonly DraftIssue[];
  status: LiveQuoteStatus;
  needsAdvertiser: NeedsAdvertiserStub | null;
  ambiguous: { error: string } | null;
  /** The quote that prices the current draft (status 'current'); null otherwise. */
  quote: MediaPlanQuote | null;
}

/** The reason box for a flagged line: one per audience × unit × direction (see planDraft `reasonKey`). */
export const reasonField = (reasonKey: string): string => `reason-${reasonKey.replace(/:/g, '-')}`;

export function planChecks({ draft, issues, status, needsAdvertiser, ambiguous, quote }: PlanChecksInput): PlanCheck[] {
  const checks: PlanCheck[] = issues.map((issue) => ({ id: `issue:${issue.field}:${issue.message}`, tone: 'fix', message: issue.message, field: issue.field }));

  if (status === 'needs-advertiser' && needsAdvertiser) {
    checks.push({ id: 'advertiser:new', tone: 'fix', message: `${needsAdvertiser.name} isn't in the advertiser list yet. Add it to price the plan.`, field: 'advertiser' });
  }
  if (status === 'ambiguous' && ambiguous) {
    checks.push({ id: 'advertiser:ambiguous', tone: 'fix', message: `“${draft.advertiserName.trim()}” matches more than one advertiser. Pick one.`, field: 'advertiser' });
  }

  if (quote) {
    // One check per reason box, however many findings share it.
    const idsByKey = segmentIdsByKey(draft.segments);
    const seen = new Set<string>();
    for (const f of findingsMissingReasons(draft, quote.guardrails ?? [])) {
      const key = reasonKeyForFinding(f, idsByKey);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      checks.push({ id: `reason:${key}`, tone: 'fix', message: reasonCheckMessage(f), field: reasonField(key) });
    }

    if (quote.advertiser.categoryStatus === 'suggested') {
      checks.push({
        id: 'advertiser:category',
        tone: 'review',
        message: `The category for ${quote.advertiser.name} (${quote.advertiser.categoryLabel}) is still a suggestion, so its pricing context may be off.`,
        field: 'advertiser',
      });
    }
    for (const note of quote.notes ?? []) checks.push({ id: `note:${note}`, tone: 'review', message: note });
    for (const warning of quote.preview.warnings) checks.push({ id: `warning:${warning}`, tone: 'review', message: warning });
  }

  const unique = new Map<string, PlanCheck>();
  for (const c of checks) if (!unique.has(c.id)) unique.set(c.id, c);
  return Array.from(unique.values());
}

function reasonCheckMessage(f: GuardrailFinding): string {
  return `Give a reason (internal) for this rate: ${f.message}`;
}

export const fixChecks = (checks: readonly PlanCheck[]): PlanCheck[] => checks.filter((c) => c.tone === 'fix');
export const reviewChecks = (checks: readonly PlanCheck[]): PlanCheck[] => checks.filter((c) => c.tone === 'review');
